/* eslint-disable @applitools/compat/no-async-await */

import { prepareAPIRootForBrowser } from '@dazn/bootstrap-api-runtime';
import * as apiRuntime from '@dazn/bootstrap-api-runtime/browserRuntime';
import { DevModeIndicatorType } from '@dazn/bootstrap-api-runtime/dist/apiComponents/devMode/appendDevModeElementToDOM';
import {
    again,
    boot,
    createNewRelicTracer,
    eraroCodesMap,
    generateSessionId,
    getChapterName,
} from '@dazn/bootstrap-lib';

import _ from 'lodash';

import { version as bootstrapVersion } from '../package.json';

import { SIGN_OUT_POPUP } from './_nodejs/popup/processPopup';
import { getSignoutPopup } from './_nodejs/splash';
import { createAffiliateModule } from './affiliate/createAffiliateModule';
import { createAiPersonalisationModule } from './aiPersonalisation/createAiPersonalisationModule';
import { createAnalyticsModule } from './analytics';
import { createScriptTag } from './analytics/getGtmTagScript';
import { handleCommonFeEvent } from './analytics/handleCommonFeEvent';
import { createBrandModule } from './brandModule/createBrandModule';
import { createConfigurationModule } from './configuration';
import type {
    ConfigurationModuleParams,
    SafeModeConfig,
} from './configuration';
import {
    AUTH_TOKEN,
    CHECK_AND_REFRESH,
    REFRESH_TOKEN_ERRORS,
    REFRESH_TOKEN_PUBBY_ROOM,
    RTL_LANGUAGES,
    SSO_TOKEN_KEY,
    TEMP_AUTH_TOKEN,
    TEMP_NATIVE_TOKEN,
    USER_SIGNOUT_REALTIME,
    chapterNames,
    environments,
    validNativeApps,
    NATIVE_SESSION,
    NATIVE_SESSION_COOKIE_EXPIRY,
    BACK_TO_APP_CALLBACK,
    LOGIN_SUCCESS,
    LOGOUT_KEYWORD,
    USER_STATES,
    KNOWN_SESSION,
    VERSIONS,
    BRANDS,
    LAST_KNOWN_USER_STATUS,
    WEB_VIEW_IN_ACTIVE,
    WEB_VIEW_ACTIVE,
} from './configuration/constants';

import { appendMetaTagToHead } from './cookieBanner/appendScript';
import { createCookieBanner } from './cookieBanner/cookieBanner';
import getCookie from './cookieBanner/getCookie';
import setCookie from './cookieBanner/setCookie';

import { createDevModeReader } from './createDevModeReader';
import { createSafeModeConfigurator } from './createSafeModeConfigurator';
import { createErrorHandlingModule } from './error-handling/createErrorHandlingModule';
import { createExperimentsModule } from './experiments';
import { USERPROFILE_API_FLAG } from './fflag/constants';
import { createFeatureFlagModule } from './fflag/createFeatureFlagModule';
import { getBrowserLanguages } from './getBrowserLanguages';
import { getContentfulConfig } from './getContentfulInfo';
import { createInitializationModule } from './initialization';
import { createCustomEventModule } from './initialization/createCustomEventModule';
import { createMonitoringModule } from './monitoringModule/createMonitoringModule';
import { createNavigationModule } from './navigation/createNavigationModule';
import { getLanguageFromURL } from './navigation/locale/getLanguage';
import { normalizeChapter } from './navigation/utils/normalizeChapter';

import { createOptimizelyAndFeaturevisorInstances } from './optimizely/createOptimizelyAndFeaturevisorInstances';

import { createPubbyModule } from './pubbyModule/createPubbyModule';
import { createPublicApiModule } from './public-api/createPublicApiModule';
import type { PublicAPIDeps } from './public-api/lib/PublicAPIDeps';
import { createSplashModule } from './splash/index';
import { createStartupModule } from './startup';
import { createUserModule } from './user';
import { handleTokenReset } from './user/handleTokenReset';
import { decodeToken } from './user/token';

const getAsyncNULL = () => Promise.resolve(null);
const MOON = 'moon';
const MENA = 'mena';

export interface CreateMainModulesProps {
    __HACK__languageStorage: boolean;
    configurationJSON: ConfigurationModuleParams;
    manufacturer: string;
    asyncStorage: boot.storage.AsyncStorage;
    targetVersion: string;
    onReady: () => Promise<boot.device.DeviceMetadata>;
    devModeIndicatorVariant: DevModeIndicatorType;
    authChapterName: boot.config.ChapterName;
    maxSplashScreenVisibilityTime: number | null;
    selectorsExcludedFromCleaning: string[] | null;
    splashScreenBehaviour: 'remove' | 'hide';
    applicationType: string;
    launchURL: string;
    exitApp: undefined | (() => void);
    restartApp: () => void;
    getLanguages: typeof getBrowserLanguages;
    handleFeEvent: typeof handleCommonFeEvent;
    fetchDeviceId: null | (() => Promise<string>);
    getAdvertisingDeviceId: null | (() => Promise<string | null>);
    fetchPartnerAccessCode: null | (() => Promise<boot.config.PACResponse>);
    getPlayerTarget: PublicAPIDeps['getPlayerTarget'];
    textToSpeechAPI: null | boot.accessibilityTools.TestToSpeechAPI;
    closedCaptionsAPI: null | boot.accessibilityTools.ClosedCaptionsAPI;
    featurevisorDatafileURL: string;
}

type _Window = typeof window;
export const createMainModules = (
    window: _Window,
    {
        __HACK__languageStorage,
        configurationJSON,
        manufacturer,
        asyncStorage,
        targetVersion,
        getLanguages,
        onReady,
        fetchDeviceId,
        devModeIndicatorVariant,
        authChapterName,
        maxSplashScreenVisibilityTime,
        selectorsExcludedFromCleaning,
        splashScreenBehaviour,
        applicationType,
        handleFeEvent,
        getAdvertisingDeviceId,
        fetchPartnerAccessCode,
        getPlayerTarget,
        launchURL,
        exitApp,
        restartApp,
        textToSpeechAPI,
        closedCaptionsAPI,
        featurevisorDatafileURL,
    }: CreateMainModulesProps,
) => {
    const usePerf =
        typeof window.performance !== 'undefined' &&
        ['now', 'mark', 'measure'].every(key => key in window.performance);

    let fvInstance: any;

    let isNativeSessionActive = false;
    let sessionParams: Record<string, string> = {};
    let backToAppUrl = '';

    const cookieBanner = createCookieBanner(window);

    if (usePerf) {
        window.performance.mark('bootstrap:start');
    }
    const isProduction =
        configurationJSON.environment === environments.PRODUCTION;
    const sessionId = generateSessionId();
    const nrTrace = createNewRelicTracer({
        useConsole: !isProduction,
        tracerName: 'boot.launch',
        sessionId,
        now: usePerf ? () => window.performance.now() : () => Date.now(),
    });
    const devModeReader = createDevModeReader(asyncStorage);
    const traceAll = nrTrace.trace('all');
    let catalogueButtonIntevalId: NodeJS.Timeout;

    const gtmContainerMap: Record<
        boot.brand.BrandName,
        { prod: string; nonProd: string }
    > = {
        dazn: {
            prod: 'GTM-N329VR8',
            nonProd: 'GTM-WDPTMX4',
        },
        kayo: {
            prod: 'GTM-KWKSR8Q8',
            nonProd: 'GTM-K9HVCP3K',
        },
        binge: {
            prod: 'GTM-5P88FPTS',
            nonProd: 'GTM-N4XR3C6M',
        },
        foxtelgnc: {
            prod: 'GTM-KBB58J5P',
            nonProd: 'GTM-WQ8NS8SQ',
        },
        foxsports: {
            prod: 'GTM-W7Z9W6TG',
            nonProd: 'GTM-57KJ4DPC',
        },
    };

    // making Beta websites non-indexable
    if (window.location.hostname?.includes('beta')) {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        meta.classList.add('protected');
        document.head.appendChild(meta);
    }

    document.addEventListener('bootstrapInitialized', () => {
        const country = startupModule.getStartupDataCountry()?.toUpperCase();
        const language =
            window?.dazn?.language ??
            window?.localStorage.getItem('UI.language');
        function getParentalToolsData() {
            try {
                const jsonData = featuresModule.getVariableStringAnonymous(
                    'f_ar_parental_tools_bet',
                    'jsondata',
                );
                return jsonData && JSON.parse(jsonData);
            } catch (e) {
                return {};
            }
        }
        const data = getParentalToolsData();

        if (!data) return;

        const locale = `${language}-${country}`;
        const content = data[locale];
        if (content) {
            const descriptionAttr = { name: 'description', content };
            appendMetaTagToHead(descriptionAttr);
        }
    });

    const onEndAll = () => {
        traceAll();
        if (!usePerf) {
            return;
        }
        window.performance.mark('bootstrap:end');
        window.performance.measure(
            'bootstrap',
            'bootstrap:start',
            'bootstrap:end',
        );

        // on some older browsers measure and mark functions return undefined
        // we need to use this kind of syntax to get performance data
        const measure = window.performance.getEntriesByName('bootstrap')?.pop();
        const endMark = window.performance
            .getEntriesByName('bootstrap:end')
            ?.pop();

        if (measure && endMark) {
            console.info(`✅ Bootstrap init: ${measure.duration}ms`);
            console.info(`✅ App launch: ${endMark.startTime}ms`);
        }
        const bootstraInitializationEvent = new CustomEvent(
            'bootstrapInitialized',
        );
        document.dispatchEvent(bootstraInitializationEvent);
    };
    const onEndSyncPhase = nrTrace.trace('init');

    const targetName = configurationJSON.target;
    nrTrace.addRelease('bootstrap', bootstrapVersion);
    nrTrace.addRelease('target', `${targetName}_${targetVersion}`);

    nrTrace.extendCustomAttributes({
        target: targetName,
        'version.bootstrap': bootstrapVersion,
        'version.target': targetVersion,
    });

    const readyPromise = nrTrace.wrapAsyncFn('onReady', onReady)();

    const getCurrentChapterName = () =>
        getChapterName(navigationModule.getCurrentChapter());

    const configuration = createConfigurationModule(configurationJSON);
    const initializationModule = createInitializationModule({
        asyncStorage: {
            load: asyncStorage.getItem,
            save: asyncStorage.setItem,
        },
        deviceDetails: {
            platform: configurationJSON.target,
            manufacturer,
        },
        config: {
            bootstrapVersion,
            targetVersion,
            environment: configurationJSON.environment,
            environmentGroup: configurationJSON.environmentGroup,
            sessionId,
        },
        getCurrentUrl: () => window.location.href,
        fetchDeviceId: fetchDeviceId ?? undefined,
    });

    const affiliateModule = createAffiliateModule(window);

    function shouldRefreshApp() {
        //Web Token: Token being used in web reference (dazn.auth.token)
        //Native Token: Token being set by native in localStorage.
        const webToken = window.dazn.auth.token;
        //if user is not loggedin webview but logged in native app, restart app.
        if (webToken === '') return true;
        const decodedWebToken = decodeToken(webToken);
        const decodedNativeToken = decodeToken(
            localStorage.getItem(configurationJSON.tokenStorageKey) || '',
        );

        const webEntitlements = decodedWebToken.entitlements.entitlementSets;

        const nativeEntitlements =
            decodedNativeToken.entitlements.entitlementSets;

        // If entitlements are not of the same length, refresh token
        if (webEntitlements.length !== nativeEntitlements.length) {
            return true;
        }

        // Sort both arrays of entitlements based on the 'id' field
        const sortedwebEntitlements = _.sortBy(webEntitlements, 'id');
        const sortednativeEntitlements = _.sortBy(nativeEntitlements, 'id');

        // Compare each object using lodash
        for (let i = 0; i < sortedwebEntitlements.length; i++) {
            // Check if objects are equal using lodash's isEqual
            if (
                !_.isEqual(
                    sortedwebEntitlements[i],
                    sortednativeEntitlements[i],
                )
            ) {
                return true;
            }
        }

        // If all entitlements are the same, don't refresh the token
        return false;
    }

    const nativeFeatures = () => ({
        messageToWeb: (eventName: string, payload: any) => {
            if (eventName === AUTH_TOKEN && payload && payload.token) {
                userModule.setToken(payload.token);
                restartApp();
            }
            if (eventName === CHECK_AND_REFRESH && shouldRefreshApp()) {
                restartApp();
            }
            if (
                eventName === WEB_VIEW_IN_ACTIVE ||
                eventName === WEB_VIEW_ACTIVE
            ) {
                window.daznNativeBridge.messageToWeb(eventName, payload);
            }
        },
        messageToNative: (
            eventName: string,
            payload: Record<string, string>,
        ) => {
            window.daznNativeBridge.messageToNative(eventName, payload);
        },
    });

    const logoutUserAndRedirectToSignIn = () => {
        clearInterval(catalogueButtonIntevalId);
        if (window.sessionStorage) {
            window.sessionStorage.clear();
        }
        window.dazn.auth.token = '';
        localStorage.removeItem('partnerToken');
        (global as any).PARTNER_TOKEN = '';
        window.location.href = '/welcome';
    };

    const { isNative, nativeApp } = initializationModule.checkNativeAppStatus();
    const { partnerApp } = initializationModule.checkPartnerApp();

    const onRealtimeLogout = () => {
        interface RefreshTokenErrorProps {
            category: string;
            code: string;
            internal: string;
        }
        const token = userModule.getToken();
        const userId = userModule.getDecodedJWT()?.user;

        if (!token && !userId) return;

        const onRefreshTokenViewMessage = () => {
            const { errorStatus, errorId, errorCategory } =
                REFRESH_TOKEN_ERRORS;

            const refreshAccessToken = async () => {
                try {
                    const oldJwt = userModule.getDecodedJWT();

                    await userModule.refreshExistingTokenAndUpdateProfile({
                        force: true,
                    });

                    const newJwt = userModule.getDecodedJWT();

                    const isAdobeLogout =
                        oldJwt?.providerName === 'adobetve' &&
                        newJwt?.providerName !== 'adobetve';

                    const isAdobeSSOLogin =
                        oldJwt?.providerName !== 'adobetve' &&
                        newJwt?.providerName !== 'adobetve' &&
                        Boolean(newJwt?.isAdobeActiveSession);

                    if (isAdobeLogout) {
                        window.dispatchEvent(new CustomEvent('AdobeLogout'));
                    }

                    if (isAdobeSSOLogin) {
                        window.dispatchEvent(new CustomEvent('AdobeLogin'));
                    }
                } catch (err: unknown) {
                    const error = err as RefreshTokenErrorProps;
                    if (
                        error.category ===
                            errorCategory.REFRESH_TOKEN_SERVICE &&
                        error.code === errorId.PLAYBACK_UNAUTHORIZED_RAT &&
                        error.internal === errorStatus.UNAUTHORISED
                    ) {
                        featuresModule.isEnabledAuthenticated(
                            'f_ar_realtime_signout_popup',
                        ) && !isNative
                            ? injectPopup()
                            : logoutUserAndRedirectToSignIn();
                    }
                }
            };

            refreshAccessToken();
        };

        pubbyModule.subscribePubbyRoom(
            userId as string,
            REFRESH_TOKEN_PUBBY_ROOM,
            false,
            onRefreshTokenViewMessage,
        );
    };

    const featuresModule = createFeatureFlagModule({
        currentEnvironment: configurationJSON.environment,
        environmentGroup: initializationModule.getEnvironmentGroup(),
        getViewerId: () => userModule.getProfile().ViewerId ?? 'N/A',
        getDeviceId: initializationModule.getDeviceId,
        getDeviceType: () =>
            startupModule.getStartupData()?.UserDevice.Type ?? 'unknown',
        getPlatform: initializationModule.getPlatform,
        getAccountStatus: () => userModule.getUserStatus() ?? 'unknown',
        getBrowser: () =>
            startupModule.getStartupData()?.UserDevice.Browser ?? 'unknown',
        getBrowserVersion: () =>
            startupModule.getStartupData()?.UserDevice.BrowserVersion ?? '',
        getUserCountryCode: () =>
            userModule.isAuthorized()
                ? userModule.getContentCountry()
                : startupModule.getStartupDataCountry() ?? '',
        getUserLanguage: () => navigationModule.getLanguage(),
        getBillingCountry: () => userModule.getSignUpCountry().toLowerCase(),
        getGeolocatedCountry: () =>
            startupModule.getStartupData()?.Region?.GeolocatedCountry ?? '',
        getGeolocatedZipcode: () =>
            startupModule.getStartupData()?.Region?.GeolocatedZip ?? '',
        getOs: () => startupModule.getStartupData()?.UserDevice.Os ?? 'unknown',
        getChapterName: getCurrentChapterName,
        getFullURL: () => window.location.href,
        getUserConsent: cookieBanner.getUserConsent,
        getIsDevModeEnabled: devModeReader.isEnabled,
        getUserNflStatus: () =>
            userModule.getDecodedJWT()?.productStatus?.NFL ?? '',
        getUserNhlStatus: () =>
            userModule.getDecodedJWT()?.productStatus?.NHL ?? '',
        getUserBillingRouting: () =>
            userModule.getDecodedJWT()?.billingRouting ?? '',
        getAnyOfTheProductGroupIsActivePaid: () =>
            Object.values(
                userModule.getDecodedJWT()?.productStatus ?? {},
            )?.some(productGroupStatus =>
                ['activegrace', 'activepaid', 'freetrial', 'paused'].includes(
                    productGroupStatus.toLocaleLowerCase(),
                ),
            ),
        getUserState: () => retriveUserStateFromJwt(userModule.getDecodedJWT()),
        asyncStorage,
        getNativeAppName: () => (isNative ? nativeApp : ''),
        getBrandName: () => brandModule.getBrandName(),
        getUserType: () => userModule.getUserType(),
    });

    const brandModule = createBrandModule({
        currentEnvironment: configurationJSON.environment,
        getDeviceId: initializationModule.getDeviceId,
    });

    const { getSafeModeConfig, readSafeModeFlag } = createSafeModeConfigurator(
        configurationJSON.safeMode,
        brandModule.getBrandName,
    );

    const analyticsModule = createAnalyticsModule({
        getViewerId: () => userModule.getProfile().ViewerId,
        handleFeEvent,
        getCurrentChapterName,
        getCurrentChapterVersion: () => {
            const currentChapterName = getCurrentChapterName();
            const versions = initializationModule.getVersions();
            return versions[currentChapterName];
        },
        getCountry: () => ({
            billingCountry: (userModule.getSignUpCountry() ?? '').toLowerCase(),
            geoCountry: startupModule.getStartupDataCountry() ?? '',
            contentCountry: userModule.isAuthorized()
                ? userModule.getContentCountry()
                : startupModule.getStartupDataCountry() ?? '',
        }),
        getUserType: () => userModule.getUserType(),
        getUserStatus: () => userModule.getUserStatus(),
        getAdvertisingDeviceId: getAdvertisingDeviceId ?? getAsyncNULL,
        getBrandName: brandModule.getBrandName,
        getSessionId: initializationModule.getSessionId,
    });

    // Helper function to fetch partner token with custom headers
    const fetchPartnerToken = (
        url: string,
        token: string,
        viewerId: string,
    ) => {
        if (!token || !url || !viewerId) return;

        const PARTNER_TOKEN = 'partnerToken';
        const SECONDS_TO_MILLISECONDS = 1000;

        const storedToken = localStorage.getItem(PARTNER_TOKEN);

        if (storedToken) {
            const decodedToken = decodeToken(storedToken);

            // Validate the existing token
            const isTokenValid =
                decodedToken &&
                decodedToken.sub === viewerId &&
                Date.now() < decodedToken.exp * SECONDS_TO_MILLISECONDS;

            if (isTokenValid) {
                (global as any).PARTNER_TOKEN = storedToken;
                return;
            }
        }

        const headers: Record<string, string> = {
            Authorization: 'Bearer ' + token,
        };

        // Add custom common headers if enabled
        const isCustomHeadersEnabled =
            featuresModule.isEnabledAnonymous('f_ar_custom_common_headers') ??
            false;
        if (isCustomHeadersEnabled) {
            headers['X-BRAND'] = brandModule.getBrandName();
            // Get daznId from the decoded token
            const decodedToken = decodeToken(token);
            const daznId = decodedToken?.user;
            if (daznId) {
                headers['X-DAZNID'] = daznId;
            }
        }

        const executeFetch = () =>
            fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify({}),
                keepalive: true,
            })
                .then(res => res.json())
                .then(data => {
                    if (data && data.token) {
                        (global as any).PARTNER_TOKEN = data.token;
                        localStorage.setItem(PARTNER_TOKEN, data.token);
                    }
                    return data;
                });

        return again(executeFetch, {
            retries: 2,
        }).catch(err =>
            console.log(
                '[PARTNER_TOKEN] Error getting the partner token',
                String(err),
            ),
        );
    };

    const errorHandlingModule = createErrorHandlingModule({
        config: {
            restrictedCountryChapterName: chapterNames.LANDING_PAGE,
            errorChapterName: chapterNames.ERROR,
            authChapterName,
        },
        metaData: {
            setCurrentChapter: chapter =>
                navigationModule.setCurrentChapter(chapter),
            sendErrorEvent: analyticsModule.sendErrorEvent,
        },
        getSafeModeConfig,
        fetch: window.fetch,
        getVariableIntegerAnonymous: featuresModule.getVariableIntegerAnonymous,
        getBrandName: brandModule.getBrandName,
    });

    const startupModule = createStartupModule({
        getGuid: initializationModule.getGuid,
        sessionId: sessionId,
        environment: configurationJSON.environment,
    });

    const experimentsModule = createExperimentsModule(
        asyncStorage.getItem,
        asyncStorage.setItem,
        startupModule.getAbTests,
        () => userModule.getProfile().ViewerId,
    );

    const retriveUserStateFromJwt = (userJwt: boot.user.UserToken | null) => {
        if (!userJwt) return USER_STATES.ANONYMOUS;
        if (userJwt?.ut)
            return userJwt?.ut === 'P'
                ? USER_STATES.PREMIUM
                : USER_STATES.FREEMIUM;
        // Failover logic for when JWT doesn't have ut property, including transition during go live.
        if (
            Object.values(userJwt?.productStatus ?? {})?.some(
                productGroupStatus =>
                    [
                        'activegrace',
                        'activepaid',
                        'freetrial',
                        'paused',
                    ].includes(productGroupStatus.toLocaleLowerCase()),
            )
        ) {
            return USER_STATES.PREMIUM;
        } else {
            return USER_STATES.FREEMIUM;
        }
    };

    const retriveUserStatusFromJwt = (userJwt: boot.user.UserToken | null) =>
        userJwt?.userstatus ?? 'unknown';

    const retrieveSSOTokenOrDefault = async () => {
        const urlParams = new URLSearchParams(window.location?.search);
        const nativeValue = urlParams.get(TEMP_NATIVE_TOKEN);
        const tempAuthToken = urlParams.get(TEMP_AUTH_TOKEN);
        const endPointUrl =
            startupModule.getStartupData().ServiceDictionary?.DaznAccessToken
                ?.Versions?.v1?.ServicePath;
        const ssoTokenFromURL = urlParams.get(SSO_TOKEN_KEY);

        const tempAuthTokenEndPoint =
            startupModule.getStartupData().ServiceDictionary
                ?.TemporaryTokenExchange?.Versions?.v1?.ServicePath;

        let accessToken;
        try {
            if (endPointUrl && nativeValue) {
                accessToken = await initializationModule.getNativeTempToken(
                    endPointUrl,
                );
            }
            if (
                tempAuthTokenEndPoint &&
                tempAuthToken &&
                tempAuthToken.toLowerCase() !== LOGOUT_KEYWORD
            ) {
                accessToken = await initializationModule.getTempAuthToken(
                    tempAuthTokenEndPoint,
                    brandModule.getBrandName(),
                );
            }
        } catch (e) {
            console.error(e);
        }

        if (tempAuthToken && tempAuthToken?.toLowerCase() === LOGOUT_KEYWORD) {
            asyncStorage.removeItem(configurationJSON.tokenStorageKey);
            return null;
        }

        const token =
            ssoTokenFromURL ||
            accessToken ||
            (await asyncStorage.getItem(configurationJSON.tokenStorageKey));

        return token;
    };

    const isRtlEnabled = () => {
        const languageFromURL = getLanguageFromURL(window.location.pathname);
        const language = window?.dazn?.language || languageFromURL || 'en';

        return RTL_LANGUAGES.indexOf(language.toLowerCase()) > -1;
    };
    const checkOriginAndInjectRTLInBody = () => {
        if (isRtlEnabled())
            document.getElementsByTagName('body')[0].setAttribute('dir', 'rtl');
    };

    const isNativeCountry = (country: string) =>
        configuration.countries.native.includes(country);
    const isMenaCountry = (country: string) =>
        [
            'bh',
            'om',
            'qa',
            'dz',
            'sa',
            'ae',
            'kw',
            'iq',
            'jo',
            'lb',
            'ps',
            'ly',
            'eg',
            'ma',
            'tn',
            'ye',
        ].includes(country);
    const getNativeCountryOrMoon = (country: boot.config.AnyCountry) =>
        isNativeCountry(country)
            ? (country as boot.config.NativeCountryOrMoon)
            : MOON;

    const getNativeCountryOrMoonOrMena = (country: boot.config.AnyCountry) =>
        isMenaCountry(country)
            ? MENA
            : isNativeCountry(country)
            ? (country as boot.config.NativeCountryOrMoon)
            : MOON;

    const getUserProfileUrl = () => {
        const version = (featuresModule.getVariableStringAnonymous(
            USERPROFILE_API_FLAG,
            'version',
        ) ?? 'v3') as boot.config.Versions;

        return startupModule.getApiUrl(
            'UserProfile',
            VERSIONS[version] ? VERSIONS[version] : 'v3',
        );
    };

    const userModule = createUserModule({
        logOutUrl: () => startupModule.getApiUrl('SignOut', 'v1'),
        userProfileUrl: getUserProfileUrl,
        load: () => retrieveSSOTokenOrDefault(),
        remove: () =>
            asyncStorage.removeItem(configurationJSON.tokenStorageKey),
        save: value =>
            asyncStorage.setItem(configurationJSON.tokenStorageKey, value),

        deviceId: initializationModule.getDeviceId,
        sessionId: initializationModule.getSessionId,
        onTokenReset: () =>
            handleTokenReset({
                checkRegionAvailability: startupModule.checkRegionAvailability,
                getBrandName: brandModule.getBrandName,
                setCurrentChapter: navigationModule.setCurrentChapter,
                onLogoutChapterConfig: configuration.chapters.onLogoutChapter,
            }),
        fetch: window.fetch,
        getGuid: initializationModule.getGuid,
        getDevMode: devModeReader.isEnabled,
        getApiUrl: startupModule.getApiUrl,
        defaultActiveUserTypes: configurationJSON.defaultActiveUserTypes,
        getLanguage: () => navigationModule.getLanguage(),
        environment: configurationJSON.environment,
        geoCountry: startupModule.getStartupDataCountry,
        getNativeCountryOrMoonOrMena: getNativeCountryOrMoonOrMena,
        getSafeModeConfig,
        getDeviceId: initializationModule.getDeviceId,
        getBrand: () => brandModule.getBrandName(),
        featuresModule,
    });

    const monitoringModule = createMonitoringModule({
        isEnabledAnonymous: featuresModule.isEnabledAnonymous,
        getVariableStringAnonymous: featuresModule.getVariableStringAnonymous,
        isProduction: isProduction,
    });

    const pubbyModule = createPubbyModule({
        getStartupData: startupModule.getStartupData,
        getUserToken: userModule.getToken,
    });

    const navigationModule = createNavigationModule({
        window,
        configuration,
        startupModule: startupModule,
        userModule: userModule,
        errorModule: errorHandlingModule,
        initializationModule: initializationModule,
        getBrandName: brandModule.getBrandName,
        features: {
            isEnabledAnonymousOptimizely: featuresModule.isEnabledAnonymous,
            getVariableStringAnonymousOptimizely:
                featuresModule.getVariableStringAnonymous,
        },
        util: {
            getNativeCountryOrMoon,
            getNativeCountryOrMoonOrMena,
            getResourceStringsEndpoint: () =>
                startupModule.getApiUrl('ResourceStrings', 'v1'),
            selectorsExcludedFromCleaning: selectorsExcludedFromCleaning ?? [],
        },
        getCustomHeaders: () => ({
            isCustomHeadersEnabled:
                featuresModule.isEnabledAnonymous(
                    'f_ar_custom_common_headers',
                ) ?? false,
            brandName: brandModule.getBrandName(),
            daznId: userModule.getDecodedJWT()?.user,
        }),
        storageModule: __HACK__languageStorage
            ? {
                  // DIRTY HACK WARNING
                  // ------------------
                  //
                  // In order to fix https://livesport.atlassian.net/browse/MESS-409 we're
                  // making sure that TV devices won't use `UI.language` stored in
                  // localStorage.
                  //
                  // saveLanguage: (lang: boot.config.Language) =>
                  // storageModule.setItem(
                  // configurationJSON.UILanguageKey,
                  // lang,
                  // ),
                  // loadLanguage: () =>
                  // storageModule.getItem(configurationJSON.UILanguageKey),
                  saveLanguage: () => Promise.resolve(),
                  loadLanguage: () => Promise.resolve(null),
              }
            : {
                  saveLanguage: lang =>
                      asyncStorage.setItem(
                          configurationJSON.UILanguageKey,
                          lang,
                      ),
                  loadLanguage: () =>
                      asyncStorage.getItem(configurationJSON.UILanguageKey),
              },
    });

    const aiPersonalisationModule = createAiPersonalisationModule({
        isFeatureEnabled: () =>
            featuresModule.isEnabledAnonymous('f_ai_rails_personalisation'),
        getEndpointUrl: () => startupModule.getApiUrl('WarmUpService', 'v1'),
        getToken: userModule.getToken,
        getCountry: () => userModule.getContentCountry().toLowerCase(),
        getLanguage: navigationModule.getLanguage,
        fetch: window.fetch,
    });

    let isLoginSuccessListenerAdded = false;

    const days = 365;
    const hours = 24;
    const minutes = 60;

    function registerLoginHandler() {
        if (!isLoginSuccessListenerAdded) {
            window.addEventListener(LOGIN_SUCCESS, () => {
                const brandName = brandModule.getBrandName();
                const isFoxsports = brandName === BRANDS.FOXSPORTS;

                // Skip post-login initialization for foxsports as it's a prelogin-only brand
                if (!isFoxsports) {
                    const userToken = userModule.getToken();
                    fetchPartnerToken(
                        startupModule.getStartupData().ServiceDictionary
                            .PartnerToken.Versions.v1.ServicePath,
                        userToken,
                        userModule.getDecodedJWT()?.viewerId || '',
                    );
                    //Initialise Pubby with user token.
                    pubbyModule.authenticatePubby(userToken);
                    aiPersonalisationModule.start();
                }
                //Add cookie to know that user has logged in at least once on this device.
                setCookie(KNOWN_SESSION, 'true', days * hours * minutes);
                //Set UserState post login into cookie - to be updated on every login.
                const userState = retriveUserStatusFromJwt(
                    userModule.getDecodedJWT(),
                );
                setCookie(
                    LAST_KNOWN_USER_STATUS,
                    userState,
                    days * hours * minutes,
                );
            });
            isLoginSuccessListenerAdded = true;
        }
        monitoringModule.triggerIdentify();
    }

    navigationModule.lifecycleEvents.onChapterDidLoad(
        () => {
            const versions = initializationModule.getVersions();
            const currentChapterName = getCurrentChapterName();
            const chapterRegion = getNativeCountryOrMoon(
                startupModule.getStartupDataCountry(),
            );

            const currentChapterVersion =
                versions[currentChapterName] || 'unknown';

            nrTrace.addRelease(
                `chapter.${currentChapterName}`,
                `${currentChapterVersion} (${targetName}, ${chapterRegion})`,
            );

            analyticsModule.sendChapterLoadEvent();

            const brandName = brandModule.getBrandName();
            const isFoxsports = brandName === BRANDS.FOXSPORTS;

            // Skip post-login logic for foxsports as it's a prelogin-only brand
            if (!isFoxsports) {
                onRealtimeLogout();
                registerLoginHandler();
                //If user is logged in already, update the cookie expiry to +1 year.
                if (userModule.isAuthorized()) {
                    setCookie(KNOWN_SESSION, 'true', days * hours * minutes);
                    //If user is logged in, add userStatus from JWT to cookie for targeting purposes. If cookie already exists, update to +1 year on every chapter load
                    const userState = retriveUserStatusFromJwt(
                        userModule.getDecodedJWT(),
                    );
                    setCookie(
                        LAST_KNOWN_USER_STATUS,
                        userState,
                        days * hours * minutes,
                    );
                }
            }
        },
        { persist: true },
    );

    const splashModule = createSplashModule({
        lifecycle: navigationModule.lifecycleEvents,
        scrollToTop: () => {
            window.scrollTo(0, 0);
        },
        currentChapter: getCurrentChapterName,
        maxSplashScreenVisibilityTime,
    });

    const removeSplashScreen =
        splashScreenBehaviour === 'remove'
            ? splashModule.removeSplashScreen
            : splashModule.hideSplashScreen;

    const injectPopup = () => {
        const { createCustomEvent, dispatchEvent } = createCustomEventModule();
        const body = document.getElementsByTagName('body')[0];

        const resourceStrings = window.dazn.resourceStringsData.Strings;

        const texts = {
            header: resourceStrings.web_signout_in_seconds_header,
            body: resourceStrings.web_signout_in_seconds_body,
            description: resourceStrings.web_signout_in_seconds_supportlink,
            close: resourceStrings.web_signout_in_seconds_cta,
        };

        document
            .querySelectorAll(`[id="${SIGN_OUT_POPUP.ID}"]`)
            .forEach(el => el?.remove());

        body.insertAdjacentHTML('beforeend', getSignoutPopup(texts));

        body.style.setProperty('overflow', 'hidden', 'important');

        dispatchEvent(document, createCustomEvent(USER_SIGNOUT_REALTIME, {}));

        const duration = 100;
        const catalogueButtonClickInterval = 2000;

        // Added this as the button click was not working intermittently
        const checkButton = setInterval(() => {
            const closeButton = document.querySelector(
                '.bootstrap-close-button',
            );
            const closeIcon = document.querySelector('.bootstrap-close-icon');
            if (closeButton && closeIcon) {
                closeButton.addEventListener(
                    'click',
                    logoutUserAndRedirectToSignIn,
                );

                closeIcon.addEventListener(
                    'click',
                    logoutUserAndRedirectToSignIn,
                );
                clearInterval(checkButton);
            } else {
                console.log('Close button not found yet');
            }
        }, duration);

        const clickCatalogueButton = () => {
            const container = document.querySelector(
                '[data-test-id="BUTTON_CONTAINER"]',
            );
            const button = container?.querySelector(
                '[data-test-id="BUTTON"]',
            ) as HTMLElement | null;
            if (button) {
                clearInterval(catalogueButtonIntevalId);
                button.click();
                const notificationLayer =
                    document.getElementById('notification-layer');
                notificationLayer?.remove();
            }
        };
        clickCatalogueButton();

        catalogueButtonIntevalId = setInterval(
            clickCatalogueButton,
            catalogueButtonClickInterval,
        );
    };

    const getUrlParams = (): { [key: string]: string } => {
        const urlParams = new URLSearchParams(window.location?.search);
        const paramsObject: { [key: string]: string } = {};

        urlParams.forEach((value, key) => {
            paramsObject[key] = value;
        });
        return paramsObject;
    };

    const getRedirectUrl = (): string => {
        const keyName = 'f_ar_native_schema';
        return featuresModule.getVariableStringAnonymous(keyName, 'schemaUrl')!;
    };

    const exchangeToken = async (token: string): Promise<string | null> => {
        try {
            const url =
                startupModule.getStartupData().ServiceDictionary
                    .GenerateTempToken.Versions.v1.ServicePath;

            const headers: Record<string, string> = {
                Authorization: `Bearer ${token}`,
            };

            // Add custom common headers if enabled
            const isCustomHeadersEnabled =
                featuresModule.isEnabledAnonymous(
                    'f_ar_custom_common_headers',
                ) ?? false;
            if (isCustomHeadersEnabled) {
                headers['X-BRAND'] = brandModule.getBrandName();
                // Get daznId from the decoded token
                const decodedToken = userModule.getDecodedJWT();
                const daznId = decodedToken?.user;
                if (daznId) {
                    headers['X-DAZNID'] = daznId;
                }
            }

            const res = await fetch(url, { headers });
            const data = await res.json();
            return data.token;
        } catch (err) {
            console.error('[BOOTSTRAP] GenerateTempToken error', String(err));
            return null;
        }
    };

    const getNativeSession = () => {
        const paramsObject = getUrlParams();
        sessionParams = paramsObject;

        const nativeSessionFromCookies = getCookie(NATIVE_SESSION);
        sessionParams = { ...nativeSessionFromCookies, ...paramsObject };
        const platform = sessionParams.platform;

        if (sessionParams.isNativeSession) {
            isNativeSessionActive = true;
        }

        if (platform && !validNativeApps.includes(platform)) {
            isNativeSessionActive = false;
            sessionParams = {};
        }
        setCookie(NATIVE_SESSION, sessionParams, NATIVE_SESSION_COOKIE_EXPIRY);

        backToAppUrl = getRedirectUrl();
    };

    const setNativeSession = (params: Record<string, string>) => {
        isNativeSessionActive = true;
        sessionParams = params;

        const updatedParams = { ...params };

        updatedParams.isNativeSession = 'true';
        setCookie(NATIVE_SESSION, updatedParams, NATIVE_SESSION_COOKIE_EXPIRY);
    };

    const backToApp = async (params: Record<string, string>) => {
        if (!backToAppUrl) return;

        // Open window BEFORE async operation to preserve gesture context
        const windowRef = window.open('', '_self');

        const url = new URL(backToAppUrl);
        const existingParams = new URLSearchParams(url.search);
        let callBackFunction: (() => void) | undefined = undefined;

        const action = params.action;
        const token = userModule.getToken();

        let exchangedToken = null;

        if (action === 'exchangeToken' && token) {
            exchangedToken = await exchangeToken(token);
        }

        if (exchangedToken) {
            existingParams.set('shortToken', exchangedToken);
        }

        Object.entries(params).forEach(([key, value]) => {
            if (typeof value !== 'function' && value) {
                existingParams.set(key, value);
            }
            if (typeof value === 'function') {
                callBackFunction = value;
            }
        });

        url.search = existingParams.toString();

        // Navigate the already-opened window
        if (windowRef) {
            windowRef.location.href = url.toString();
        } else {
            window.location.href = url.toString(); // fallback
        }

        if (callBackFunction) {
            setTimeout(() => {
                if (callBackFunction) {
                    callBackFunction();
                }
            }, BACK_TO_APP_CALLBACK);
        }
    };

    const setupPublicAPI = ({
        deviceMetadata,
        isDevModeEnabled,
        safeModeConfig,
    }: {
        deviceMetadata: boot.device.DeviceMetadata;
        isDevModeEnabled: boolean;
        safeModeConfig: SafeModeConfig;
    }) =>
        prepareAPIRootForBrowser(window)
            .useAPIComponents([
                // list all tha API modules available in the target
                apiRuntime.createAsyncStorageComponent(asyncStorage),
                apiRuntime.createDevModeComponent({
                    indicatorVariant: devModeIndicatorVariant,
                    isEnabled: isDevModeEnabled,
                    onChange: isEnabled => {
                        void devModeReader.update(isEnabled);
                        restartApp();
                    },
                }),
                apiRuntime.createLoggerComponent(
                    'Bootstrap',
                    configurationJSON.environment,
                ),
                apiRuntime.deviceMetadataComponent,
                apiRuntime.startupDeviceComponent,
                apiRuntime.createSafeModeComponent(safeModeConfig),
                apiRuntime.createUILanguageComponent(
                    navigationModule.getLanguage,
                ),
                apiRuntime.createL10nComponent(
                    navigationModule.getResourceStrings,
                ),
                apiRuntime.authTokenComponent,
                apiRuntime.feEventsComponent,
                apiRuntime.closedCaptionsComponent,
            ])
            // (Maxim) Throws in case Startup failed, EndOfTheWorld
            // TODO: think through how to gracefully fallback for API v2
            .catch(err => {
                console.error('[Bootstrap] setupPublicAPI (v2)');
                console.error(err);
                nrTrace.noticeError(err, 'from setupPublicAPI');
                return null;
            })
            .then(() => {
                const { messageToNative, messageToWeb } = nativeFeatures();
                getNativeSession();
                if (sessionParams.purchaseContextId) {
                    analyticsModule.sendNativeAppReferralEvent({
                        platform: sessionParams.platform || '',
                        manufacturer: sessionParams.manufacturer || '',
                        purchaseContextId: sessionParams.purchaseContextId,
                        referralSource: sessionParams.sourceRef,
                        inAppSourceRef: sessionParams.inAppSourceRef,
                        trackingId: sessionParams.trackingId,
                        externalTrackingID: sessionParams.externalTrackingID,
                        productGroup: sessionParams.productGroup,
                        entitlementSetId: sessionParams.entitlementSetId,
                        contextSource: sessionParams.contextSource,
                        sourceDeviceId: sessionParams.deviceId,
                        sourceSessionId: sessionParams.sessionId,
                        deepLink: sessionParams.deepLink,
                    });
                }

                createPublicApiModule({
                    window,
                    shouldTrack: true,
                    shouldLog: true,
                    deps: {
                        getHomeCurrency: userModule.getHomeCurrency,
                        getContentCountry: userModule.getContentCountry,
                        getCountry: userModule.getCountry,
                        getUserType: userModule.getUserType,
                        isVip: userModule.isVip,
                        getUserStatus: userModule.getUserStatus,
                        getAuthToken: userModule.getToken,
                        getAnonymousToken: userModule.getAnonymousToken,
                        setAnonymousToken: userModule.setAnonymousToken,
                        setAuthToken: userModule.setToken,
                        getUserProfile: userModule.getProfile,
                        setUserProfile: userModule.setProfile,
                        getEnvironment: initializationModule.getEnvironment,
                        getValidatedToken: userModule.getValidatedToken,
                        getEnvironmentGroup:
                            initializationModule.getEnvironmentGroup,
                        getPlatform: initializationModule.getPlatform,
                        getManufacturer: initializationModule.getManufacturer,
                        deviceMetadata,
                        manufacturer,
                        getInitialUrl: initializationModule.getInitialUrl,
                        getInitialUrlParameters:
                            initializationModule.getInitialUrlParameters,
                        triggerFsEvent: monitoringModule.triggerFsEvent,
                        updateBrazeUserProfile:
                            monitoringModule.updateBrazeUserProfile,
                        subscribePubbyRoom: pubbyModule.subscribePubbyRoom,
                        unsubscribePubbyRoom: pubbyModule.unsubscribePubbyRoom,
                        getPubbyEventTypes: pubbyModule.getPubbyEventTypes,
                        getBrandName: () => brandModule.getBrandName(),
                        updateBrand: brandModule.updateBrand,
                        addPubbyEventListener:
                            pubbyModule.addPubbyEventListener,
                        removePubbyEventListener:
                            pubbyModule.removePubbyEventListener,
                        getCampaignId: startupModule.getCampaignId,
                        getPromoId: startupModule.getPromoId,
                        getSessionId: initializationModule.getSessionId,
                        getDeviceId: initializationModule.getDeviceId,
                        getGuid: initializationModule.getGuid,
                        getStartupData: startupModule.getStartupData,
                        setStartupData: startupModule.setStartupData,
                        getResourceStringsData:
                            navigationModule.getResourceStrings,
                        getCurrentChapter: navigationModule.getCurrentChapter,
                        setCurrentChapter: navigationModule.setCurrentChapter,
                        getChaptersVersions: initializationModule.getVersions,
                        getChapterLanguage: navigationModule.getLanguage,
                        getApplicationType: () => applicationType,
                        getExperiments: experimentsModule.getExperiments,
                        getOneTrustConsentGroups:
                            cookieBanner.getOneTrustConsentGroups,
                        captureException: errorHandlingModule.captureException,
                        getAsyncStorage: () => asyncStorage,
                        lifecycle: navigationModule.lifecycleEvents,
                        getChapterFromPath: navigationModule.getChapterFromPath,
                        getUserExperiment: experimentsModule.getUserExperiment,
                        getUserExperimentNumber:
                            experimentsModule.getUserExperimentNumber,
                        getAnonymousExperiment:
                            experimentsModule.getAnonymousExperiment,
                        removeSplashScreen,
                        isMoonCountry: (
                            country = startupModule.getStartupDataCountry(),
                        ) => !isNativeCountry(country),
                        exitApp: exitApp,
                        fetchPartnerAccessCode,
                        refreshExistingTokenAndUpdateProfile:
                            userModule.refreshExistingTokenAndUpdateProfile,
                        getDecodedJWT: userModule.getDecodedJWT,
                        getDecodedAnonymousToken:
                            userModule.getDecodedAnonymousToken,
                        getUserConsent: cookieBanner.getUserConsent,
                        getAdvertisingDeviceId:
                            getAdvertisingDeviceId ?? getAsyncNULL,
                        fetchSafeMode: errorHandlingModule.fetchSafeMode,
                        restartApp,
                        getPlayerTarget,
                        handleFeEvent,
                        closedCaptionsAPI,
                        textToSpeechAPI,
                        // API v2
                        getDevMode: devModeReader.isEnabled,
                        enableDevMode: () => devModeReader.update(true),
                        disableDevMode: () => devModeReader.update(false),
                        resetCookieScripts: () =>
                            cookieBanner.findAndDeleteCookieScripts(
                                {
                                    environment: configurationJSON.environment,
                                    chapterLanguage:
                                        navigationModule.getLanguage(),
                                    chapterName: getChapterName(
                                        navigationModule.getCurrentChapter(),
                                    ),
                                    startupCountry:
                                        startupModule.getStartupDataCountry(),
                                },
                                featuresModule.isEnabledAnonymous(
                                    'f_ar_new_consent_cookie_script',
                                ),
                                featuresModule.getVariableStringAnonymous(
                                    'f_tcf_enablement_web',
                                    'appID',
                                ) ?? '17ee4b6b-233e-41d3-a670-2085e68960ab',
                                featuresModule.getVariableStringAnonymous(
                                    'f_tcf_enablement_web',
                                    'scriptURL',
                                ) ??
                                    'https://cdn.cookielaw.org/consent/17ee4b6b-233e-41d3-a670-2085e68960ab/otSDKStub.js',
                            ),
                        getContentfulConfig: () =>
                            getContentfulConfig(
                                featuresModule.isEnabledAnonymous(
                                    'f_ar_contentful_proxy',
                                ) ?? false,
                                featuresModule.getVariableStringAnonymous(
                                    'f_ar_contentful_proxy',
                                    'proxyUrl',
                                ) ?? '',
                                navigationModule.getLanguage(),
                                `${navigationModule.getLanguage()}-${
                                    userModule
                                        .getContentCountry()
                                        ?.toUpperCase() ||
                                    startupModule
                                        .getStartupData()
                                        ?.Region?.Country?.toUpperCase()
                                }`,
                                userModule.getCountry() ??
                                    startupModule.getStartupData()?.Region
                                        ?.Country,
                            ),
                        getViewerId: () =>
                            decodeToken(userModule.getToken())?.viewerId
                                ? decodeToken(userModule.getToken())?.viewerId
                                : null,
                        getAnonymousViewerId: () =>
                            decodeToken(userModule.getAnonymousToken())
                                ?.viewerId,
                        getUserState: () =>
                            retriveUserStateFromJwt(userModule.getDecodedJWT()),

                        optimizely: {
                            // OPTIMIZELY API
                            // ANONYMOUS
                            isEnabledAnonymousOptimizely:
                                featuresModule.isEnabledAnonymous,
                            getVariableBooleanAnonymousOptimizely:
                                featuresModule.getVariableBooleanAnonymous,
                            getVariableStringAnonymousOptimizely:
                                featuresModule.getVariableStringAnonymous,
                            getVariableIntegerAnonymousOptimizely:
                                featuresModule.getVariableIntegerAnonymous,
                            getVariationAnonymousOptimizely:
                                featuresModule.getVariationAnonymous,
                            trackAnonymousOptimizely:
                                featuresModule.trackAnonymous,
                            activateAnonymousOptimizely:
                                featuresModule.activateAnonymous,
                            // AUTHENTICATED
                            trackAuthenticatedOptimizely:
                                featuresModule.trackAuthenticated,
                            activateAuthenticatedOptimizely:
                                featuresModule.activateAuthenticated,
                            isEnabledAuthenticatedOptimizely:
                                featuresModule.isEnabledAuthenticated,
                            getVariationAuthenticatedOptimizely:
                                featuresModule.getVariationAuthenticated,
                            getVariableStringAuthenticatedOptimizely:
                                featuresModule.getVariableStringAuthenticated,
                            getVariableBooleanAuthenticatedOptimizely:
                                featuresModule.getVariableBooleanAuthenticated,
                            getVariableIntegerAuthenticatedOptimizely:
                                featuresModule.getVariableIntegerAuthenticated,
                            // ForcedVariation
                            getForcedVariationAuthenticatedOptimizely:
                                featuresModule.getForcedVariationAuthenticated,
                            getForcedVariationAnonymousOptimizely:
                                featuresModule.getForcedVariationAnonymous,
                            setForcedVariationAuthenticatedOptimizely:
                                featuresModule.setForcedVariationAuthenticated,
                            setForcedVariationAnonymousOptimizely:
                                featuresModule.setForcedVariationAnonymous,
                            // Auxiliary
                            // @ts-expect-error Type 'ListenerPayload' is not assignable to type 'T'.
                            addNotificationListener:
                                featuresModule.addNotificationListener,
                            removeNotificationListener:
                                featuresModule.removeNotificationListener,
                            setOptimizelyLogLevel:
                                featuresModule.setOptimizelyLogLevel,
                        },
                        featurevisor: {
                            setFeaturevisorLogLevels:
                                featuresModule.setFeaturevisorLogLevelsUpdated,

                            getVariableObjectAnonymous:
                                featuresModule.getVariableObjectAnonymous,

                            getVariableArrayAnonymous:
                                featuresModule.getVariableArrayAnonymous,

                            getVariableArrayAuthenticated:
                                featuresModule.getVariableArrayAuthenticated,

                            getVariableObjectAuthenticated:
                                featuresModule.getVariableObjectAuthenticated,
                        },
                        native: {
                            isNative: () => isNative,
                            appName: () => nativeApp,
                            messageToNative,
                            messageToWeb,
                        },
                        isRtlEnabled,
                        getDefaultContext: featuresModule.getDefaultAttributes,
                        featurevisorInstance: () => fvInstance,
                        sessionParams: () => sessionParams,
                        isNativeSession: () => isNativeSessionActive,

                        backToApp,
                        setNativeSession,
                        getPartnerApp: () => partnerApp,
                        getAffiliateData: affiliateModule.getAffiliateData,
                        setAffiliateData: affiliateModule.setAffiliateData,
                        clearAffiliate: affiliateModule.clearAffiliate,
                    },
                });

                checkOriginAndInjectRTLInBody();
            });

    const loadGTM = (isProduction: boolean) => {
        const envKey = isProduction ? 'prod' : 'nonProd';
        const gtmContainerId =
            gtmContainerMap[brandModule.getBrandName()][envKey];

        const gtmBaseUrl =
            featuresModule.getVariableStringAnonymous(
                'f_ar_gtm_lazy_loading',
                'gtmUrl',
            ) || undefined;

        window.document.body.appendChild(
            createScriptTag(window, gtmContainerId, gtmBaseUrl),
        );
    };

    const initAnalytics = nrTrace.wrapAsyncFn('analytics', () =>
        readyPromise.then((deviceMetadata: boot.device.DeviceMetadata) => {
            const key = 'f_ar_gtm_lazy_loading';

            const isDisableGTM = featuresModule.getVariableBooleanAnonymous(
                key,
                'disableGtm',
            );

            if (isDisableGTM) return;

            void analyticsModule.sendLaunchEvent({
                initialURL: launchURL,
                applicationType: applicationType,
                version: targetVersion,
                environment: configurationJSON.environment,
                deviceId: initializationModule.getDeviceId(),
                model: deviceMetadata.model ?? 'N/A',
                platform: configurationJSON.target,
                launchParams: {},
                externalTrackingID: sessionParams.trackingId || '',
                getBrandName: brandModule.getBrandName(),
            });

            const defaultDuration = 5000;

            if (featuresModule.isEnabledAnonymous(key)) {
                const timeOutToLoadGTM = setTimeout(() => {
                    loadGTM(isProduction);
                    clearTimeout(timeOutToLoadGTM);
                }, featuresModule.getVariableIntegerAnonymous(key, 'duration') || defaultDuration);
            } else {
                loadGTM(isProduction);
            }
        }),
    );

    const initFFlags = nrTrace.wrapAsyncFn('optimizely', () =>
        readyPromise
            .then(async (deviceMetadata: boot.device.DeviceMetadata) => {
                const { featurevisorInstance } =
                    await createOptimizelyAndFeaturevisorInstances({
                        environment: configurationJSON.environment,
                        featurevisorDatafileURL,
                        trackFeaturevisorErrors:
                            featuresModule.trackFeaturevisorErrors,
                    });

                fvInstance = featurevisorInstance;

                const featurevisorPromise = nrTrace.wrapAsyncFn(
                    'createFeaturevisorInstance',
                    () => featurevisorInstance,
                )();

                return Promise.allSettled([featurevisorPromise]).then(
                    ([featurevisor]) =>
                        featuresModule.run(
                            featurevisor.status === 'fulfilled'
                                ? featurevisor.value
                                : null,
                            deviceMetadata,
                            manufacturer,
                        ),
                );
            })
            .then(() => readSafeModeFlag(featuresModule.isEnabledAnonymous))
            .catch(() => readSafeModeFlag(() => false)),
    );
    const initDevModeReader = nrTrace.wrapAsyncFn(
        'devModeReader',
        devModeReader.readFromStorage,
    );

    const waitForAPI = async () => {
        try {
            const deviceMetadata = await readyPromise;

            const apiSetupOptions = {
                deviceMetadata,
                isDevModeEnabled: devModeReader.isEnabled(),
                safeModeConfig: getSafeModeConfig(),
            };

            return setupPublicAPI(apiSetupOptions);
        } catch (error) {
            console.warn(error);
        }
    };

    onEndSyncPhase();
    const onEndPromiseChain = nrTrace.trace('promiseChain');

    return {
        initMainModules: (): Promise<void> =>
            Promise.resolve(null)
                .then(() =>
                    Promise.all(
                        [
                            initDevModeReader,
                            nrTrace.wrapAsyncFn('splash', () =>
                                (window as any).daznSSR
                                    ? Promise.resolve()
                                    : splashModule.run(),
                            ),
                            nrTrace.wrapAsyncFn(
                                'initialization',
                                initializationModule.run,
                            ),
                        ].map(fn => fn()),
                    ),
                )
                .then(nrTrace.wrapAsyncFn('brand', brandModule.run))
                .then(() =>
                    Promise.all(
                        [
                            nrTrace.wrapAsyncFn(
                                'affiliateModule',
                                affiliateModule.init,
                            ),
                            nrTrace.wrapAsyncFn(
                                'experiments',
                                experimentsModule.run,
                            ),
                            nrTrace.wrapAsyncFn('startup', async () => {
                                // Check if user is already logged in (e.g., page reload after login)
                                let daznId: string | undefined;
                                try {
                                    const storedToken =
                                        await asyncStorage.getItem(
                                            configurationJSON.tokenStorageKey,
                                        );
                                    if (storedToken) {
                                        const decodedToken =
                                            decodeToken(storedToken);
                                        daznId = decodedToken?.user;
                                    }
                                } catch (e) {
                                    // If there's an error reading token, continue without daznId
                                }

                                return startupModule.run({
                                    shouldUseMarcoPolo:
                                        devModeReader.isEnabled(),
                                    languages: getLanguages(),
                                    customHeaders: {
                                        isCustomHeadersEnabled: true, //forcing true here as FV is not yet ready.
                                        brandName: brandModule.getBrandName(),
                                        daznId,
                                    },
                                });
                            }),
                        ].map(fn => fn()),
                    ),
                )
                .then(() => initFFlags())
                .then(
                    nrTrace.wrapAsyncFn('user', () =>
                        userModule.run({
                            _initializationModule: initializationModule,
                        }),
                    ),
                )
                .then(() =>
                    Promise.all(
                        [
                            () =>
                                fetchPartnerToken(
                                    startupModule.getStartupData()
                                        .ServiceDictionary.PartnerToken.Versions
                                        .v1.ServicePath,
                                    userModule.getToken(),
                                    userModule.getDecodedJWT()?.viewerId || '',
                                ),
                            nrTrace.wrapAsyncFn(
                                'navigation',
                                navigationModule.run,
                            ),
                            nrTrace.wrapAsyncFn('popstate', () => {
                                window.addEventListener(
                                    'popstate',
                                    navigationModule.handlePopstate,
                                );
                            }),
                            nrTrace.wrapAsyncFn('pubby', pubbyModule.run),
                            nrTrace.wrapAsyncFn('aiPersonalisation', () => {
                                aiPersonalisationModule.start();
                                return Promise.resolve();
                            }),
                        ].map(fn => fn()),
                    ),
                )
                .then(nrTrace.wrapAsyncFn('setupPublicAPI', waitForAPI))
                .then(() =>
                    Promise.all(
                        [
                            nrTrace.wrapAsyncFn(
                                'monitoring',
                                monitoringModule.run,
                            ),
                        ].map(fn => fn()),
                    ),
                )
                .then(
                    nrTrace.wrapAsyncFn('cookieBanner', () =>
                        cookieBanner.run(
                            {
                                environment: configurationJSON.environment,
                                lifecycleEvents:
                                    navigationModule.lifecycleEvents,
                                chapterLanguage: navigationModule.getLanguage(),
                                chapterName: getChapterName(
                                    navigationModule.getCurrentChapter(),
                                ),
                                startupCountry:
                                    startupModule.getStartupDataCountry(),
                            },
                            featuresModule.isEnabledAnonymous(
                                'f_ar_new_consent_cookie_script',
                            ),
                            featuresModule.getVariableBooleanAuthenticated(
                                'f_peng_liveramp_enabled',
                                'enableLiveRampAPI',
                            ),
                            featuresModule.getVariableIntegerAuthenticated(
                                'f_peng_liveramp_enabled',
                                'rampIdExpiryInMillis',
                            ),
                            featuresModule.getVariableBooleanAuthenticated(
                                'f_peng_liveramp_enabled',
                                'enablePairId',
                            ),
                            featuresModule.getVariableBooleanAuthenticated(
                                'f_peng_liveramp_enabled',
                                'enableATSD',
                            ),
                            featuresModule.getVariableStringAnonymous(
                                'f_tcf_enablement_web',
                                'appID',
                            ) ?? '17ee4b6b-233e-41d3-a670-2085e68960ab',
                            featuresModule.getVariableStringAnonymous(
                                'f_tcf_enablement_web',
                                'scriptURL',
                            ) ??
                                'https://cdn.cookielaw.org/consent/17ee4b6b-233e-41d3-a670-2085e68960ab/otSDKStub.js',
                        ),
                    ),
                ),
        loadInitialChapter: (deepLink: boot.config.DeepLink | null) =>
            Promise.resolve()
                .then(
                    nrTrace.wrapAsyncFn('deepLink', () => {
                        if (!deepLink) {
                            return null;
                        }
                        const normalChapter = normalizeChapter(deepLink, '');
                        if (normalChapter) {
                            nrTrace.addPageAction('deepLink', {
                                chapterName: normalChapter.chapterName,
                                chapterPath: normalChapter.chapterPath,
                            });
                        }
                        return deepLink;
                    }),
                )
                .then(
                    nrTrace.wrapAsyncFn(
                        'loadInitialChapter',
                        navigationModule.loadInitialChapter,
                    ),
                )
                .then(() => nrTrace.addPageAction('success'))
                .then(initAnalytics)
                .then(onEndPromiseChain)
                .then(onEndAll),
        handleInitError: (exception: unknown) =>
            Promise.resolve(null)
                .then(() => {
                    console.error('[Bootstrap] handleInitError');
                    console.error(exception);
                })
                .then(initDevModeReader)
                .then(initFFlags)
                .then(
                    nrTrace.wrapAsyncFn('setupPublicAPI', () =>
                        waitForAPI().catch(err => {
                            console.error(
                                '[Bootstrap] setupPublicAPI has failed',
                            );
                            console.error(err);
                            nrTrace.noticeError(err, 'from setupPublicAPI');
                        }),
                    ),
                )
                .then(
                    nrTrace.wrapAsyncFn('handleError', () => {
                        const error =
                            exception instanceof Error
                                ? exception
                                : new Error(String(exception));

                        nrTrace.noticeError(error, 'from initMainModules');
                        nrTrace.addPageAction('error', {
                            message: error.message,
                        });
                        errorHandlingModule.handleError(
                            undefined,
                            eraroCodesMap.code.AppLoadError,
                            undefined,
                            getCurrentChapterName(),
                            error,
                        );
                        removeSplashScreen();
                    }),
                )
                .then(initAnalytics)
                .then(onEndPromiseChain)
                .then(onEndAll),
        nrTrace,
        module: {
            analytics: analyticsModule,
            configuration: configuration,
            errorHandling: errorHandlingModule,
            initialization: initializationModule,
            navigation: navigationModule,
            fflag: featuresModule,
            user: userModule,
            splash: splashModule,
            startup: startupModule,
            experiments: experimentsModule,
            monitoring: monitoringModule,
        },
    };
};
